import { Flex, Modal, Text, Title } from '@nula/core-ui';
import { TERMS, PRIVACY } from './legal-content';
import styles from './legal-modal.module.css';

export type LegalKind = 'terms' | 'privacy';

// Canonical Terms / Privacy copy lives in legal-content.tsx, mirrored from
// nulanotes.com/terms and /privacy. Shipped in-app and styled to match Nula
// (no iframe / site chrome).
const LEGAL = { terms: TERMS, privacy: PRIVACY };

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <path d="M15 3h6v6" />
    <path d="M10 14 21 3" />
  </svg>
);

export interface LegalModalProps {
  /** Which document to show, or null to render nothing. */
  kind: LegalKind | null;
  onClose: () => void;
}

/**
 * In-app Terms / Privacy modal — renders the legal content directly (no iframe)
 * with an "open in browser" link to the canonical page. Built on core-ui Modal,
 * which handles the overlay, Escape, and focus trap.
 */
export function LegalModal({ kind, onClose }: LegalModalProps) {
  if (!kind) return null;
  const doc = LEGAL[kind];

  const header = (
    <Flex align="center" gap="sm" className={styles.header}>
      <Title level={3} className={styles.title} id="legal-modal-title">{doc.title}</Title>
      <a
        className={styles.ext}
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open in browser"
        title="Open in browser"
      >
        <ExternalIcon />
      </a>
    </Flex>
  );

  return (
    <Modal open onClose={onClose} title={header} footer={null} width={680} centered>
      <div className={styles.body}>
        <Text type="secondary" className={styles.updated}>{doc.updated}</Text>
        <div className={styles.prose}>{doc.intro}</div>
        {doc.sections.map((s) => (
          <section key={s.heading}>
            <Title level={4} className={styles.heading}>{s.heading}</Title>
            <div className={styles.prose}>{s.body}</div>
          </section>
        ))}
      </div>
    </Modal>
  );
}
